'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Field } from '@eliadmin/core/src/components/form/field';

export const signInSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
};

export function SignInForm({ onSuccess, initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      router.replace(ROUTES.MEMBER_ZONE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col items-center justify-center gap-8', 'ea-sign-in-form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2.5',
            'ea-sign-in-form__header',
          )}
        >
          <h3
            className={cn(
              'text-center text-3xl font-bold text-primary xl:text-4xl',
              'ea-sign-in-form__header-title',
            )}
          >
            {t('text190')}
          </h3>
          <p
            className={cn(
              'px-2 text-center text-sm text-primary lg:text-base',
              ' ea-sign-in-form__header-prompt',
            )}
          >
            {t('text3235')}&nbsp;
            {inDialog ? (
              <DialogClose>
                <a
                  href={ROUTES.REGISTER() ?? '#'}
                  className={cn('text-secondary', 'ea-sign-in-form__header-link')}
                >
                  {t('text3236')}
                </a>
              </DialogClose>
            ) : (
              <Link
                href={ROUTES.REGISTER() ?? '#'}
                className={cn('text-secondary', 'ea-sign-in-form__header-link')}
                prefetch={false}
              >
                {t('text3236')}
              </Link>
            )}
          </p>
        </div>

        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-8',
            'ea-sign-in-form__content',
          )}
        >
          <Field
            required
            control={form.control}
            name="email"
            type="email"
            placeholder="E-mail"
            className={{ wrapper: 'w-full' }}
          />
          <Field
            required
            control={form.control}
            name="password"
            type="password"
            placeholder={t('text191')}
            className={{ wrapper: 'w-full' }}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div
          className={cn(
            '-mt-8 flex w-full flex-col items-center justify-center gap-8',
            'ea-sign-in-form__footer',
          )}
        >
          <Button
            type="submit"
            variant={'secondary'}
            className={cn(
              'w-full text-sm font-semibold lg:text-base',
              'ea-sign-in-form__submit-button',
            )}
            isLoading={form.formState.isSubmitting}
          >
            {t('text189')}
          </Button>

          <Link
            className={cn(
              'w-full text-center text-sm font-semibold text-secondary lg:text-base',
              'ea_sign-in-form__password-reset-link ',
            )}
            href={ROUTES.PASSWORD_RESET() ?? '#'}
            prefetch={false}
          >
            {t('text3237')}
          </Link>
        </div>
      </form>
    </Form>
  );
}
